import type { ApiEvent } from '#/modules/listing/events/api/types/ApiEvent';
import type { ApiInternship } from '#/modules/listing/internships/api/types/ApiInternship';
import type { ApiJob } from '#/modules/listing/jobs/api/types/ApiJob';
import type { ApiVolop } from '#/modules/listing/volops/api/types/ApiVolop';
import type { SearchSavedListingsStore } from '#/modules/search/zustand-stores/searchSavedListingsStore';

import { useSaveListing } from './useSaveListing';
import { useSaveOrgAfterListing } from './useSaveOrgAfterListing';

type Args = {
  listing: ApiJob | ApiInternship | ApiVolop | ApiEvent;
  initiallySaved: boolean;
  /**
   * When initiallySaved is null it means we're still loading it
   */
  orgInitiallySaved: boolean | null;
  searchSavedListingsStore: SearchSavedListingsStore | null;
};

export function useSaveListingAndOrg({
  listing,
  initiallySaved,
  orgInitiallySaved,
  searchSavedListingsStore,
}: Args) {
  const {
    orgSaved,
    saveOrg,
    unsaveOrg,
    showSaveOrgTooltip,
    shouldShowSaveOrgTooltip,
    dismissSaveOrgTooltip,
  } = useSaveOrgAfterListing({
    orgId: listing.org?.id ?? null,
    orgType: listing.org?.orgType ?? null,
    orgInitiallySaved,
    searchSavedListingsStore,
  });

  const { save, saved, unsave } = useSaveListing({
    listingId: listing.id,
    listingType: listing.type,
    initiallySaved,
    orgId: listing.org?.id ?? null,
    orgType: listing.org?.orgType ?? null,
    onSaved: showSaveOrgTooltip,
    onUnsaved: dismissSaveOrgTooltip,
    searchSavedListingsStore,
    trackClickEvent: true,
  });

  return {
    save,
    saved,
    unsave,
    orgSaved,
    saveOrg,
    unsaveOrg,
    shouldShowSaveOrgTooltip,
    dismissSaveOrgTooltip,
  };
}
